import { BigNumberish, ethers, utils } from 'ethers'
import { Address, L1ChainId, TokenAddress } from '../../../types'
import { TransactionResponse } from '@ethersproject/abstract-provider'
import { store } from '../../../store'
import { updateDepositingAction } from '../../../store/deposit/actions'
import { isGasAddress } from '../../../utils/address'
import { getGatewayScheduling } from '../../../api/v1/scheduling'
import {
  ERC20_INTERFACE,
  ZKLINK_GATEWAY_CONTRACT_INTERFACE,
  ZKLINK_MAIN_CONTRACT_INTERFACE,
} from '../../../abi'
import { bn } from '../../../utils/number'
import { MAX_ERC20_APPROVE_AMOUNT } from '../../../constants/deposit'
import { getRecommendedDepositGasLimit } from '../../../utils/depositGasLimit'
import { JsonRpcProvider } from '@ethersproject/providers'
import { isAddress } from '@ethersproject/address'
import { DepositResult } from './index'
import { BigNumber } from '@ethersproject/bignumber'
import { hexlify } from 'ethers/lib/utils'
import { NetworkRegistry } from '../../../utils/networkRegistry'

export async function estimateDepositErc20FeeValue(
  provider: JsonRpcProvider,
  instance: ethers.utils.Interface,
  zklinkContract: Address,
) {
  const data = instance.encodeFunctionData('fee', [])
  return await provider.call({
    to: zklinkContract,
    data,
  })
}

export async function sendDepositETHFromEthereum(
  provider: ethers.providers.JsonRpcProvider,
  payload: {
    mainContract: string // zkLink's main contract address, e.g. "0x..."
    from: string // Sender address, e.g. "0x..."
    depositTo: string // Recipient address, e.g. "0x..."
    amount: BigNumber // Amount to deposit, e.g. BigNumber('100000000000000000000000')
    subAccountId: number // Sub-account ID, e.g. 0
    ethTxOptions?: ethers.providers.TransactionRequest
  },
): Promise<TransactionResponse> {
  const {
    mainContract,
    from,
    depositTo,
    amount,
    subAccountId,
    ethTxOptions = {},
  } = payload
  const data = ZKLINK_MAIN_CONTRACT_INTERFACE.encodeFunctionData('depositETH', [
    depositTo,
    subAccountId,
  ])
  const tx = {
    ...ethTxOptions,
    from,
    to: mainContract,
    data,
    value: amount.toString(),
  }
  return await provider.getSigner().sendTransaction(tx)
}

export async function sendApproveFromEthereum(
  provider: ethers.providers.JsonRpcProvider,
  payload: {
    from: string // Sender address, e.g. "0x..."
    mainContract: string // zkLink's main contract address, e.g. "0x..."
    tokenAddress: string // Token address, e.g. "0x..."
    amount: BigNumber // Amount to approve, e.g. BigNumber('100000000000000000000000')
  },
): Promise<number | null> {
  const { from, mainContract, tokenAddress, amount } = payload
  const allowanceData = ERC20_INTERFACE.encodeFunctionData('allowance', [
    from,
    mainContract,
  ])

  const allowance = await provider.call({
    to: tokenAddress,
    data: allowanceData,
  })

  // Check if allowance is enough to deposit.
  const isEnoughAllowance = bn(allowance).gte(amount)
  // Return null means no need to approve.
  if (isEnoughAllowance) {
    return null
  }

  // approve deposit amount for ERC20
  const data = ERC20_INTERFACE.encodeFunctionData('approve', [
    mainContract,
    MAX_ERC20_APPROVE_AMOUNT,
  ])
  const { nonce } = await provider.getSigner().sendTransaction({
    from: from,
    to: tokenAddress,
    data,
  })

  // Return nonce means deposit needs to specify the nonce + 1.
  return nonce
}

export async function sendDepositFromEthereum(
  provider: ethers.providers.JsonRpcProvider,
  deposit: {
    chainId: L1ChainId
    from: Address
    subAccountId: number
    depositTo: Address
    token: TokenAddress
    amount: BigNumberish
    mapping: boolean
    ethTxOptions?: ethers.providers.TransactionRequest
    approveDepositAmountForERC20?: boolean
  },
): Promise<DepositResult> {
  let response: TransactionResponse
  try {
    store.dispatch(updateDepositingAction(true))

    if (!isAddress(deposit.token)) {
      throw new Error('Token address is invalid')
    }
    const depositTo = utils.zeroPad(deposit.depositTo, 32)

    const currentNetwork = NetworkRegistry.byL1(deposit.chainId)
    if (!currentNetwork) {
      throw new Error(`Unknown network ${deposit.chainId}`)
    }

    let { mainContract, gateway } = currentNetwork

    // Deposit on Ethereum requires fetching the preferred gateway from the gateway scheduling system.
    if (gateway) {
      const priorityGateway = await getGatewayScheduling(
        store.getState().app.ethProperty.gateways,
      )
      if (!priorityGateway) {
        throw new Error(`No available gateway found`)
      }
      // Update mainContract with values from the preferred gateway.
      mainContract = priorityGateway.l1GatewayContract
    }

    if (!mainContract) {
      throw new Error('Main contract address is invalid')
    }

    if (isGasAddress(deposit.token)) {
      response = await sendDepositETHFromEthereum(provider, {
        mainContract,
        from: deposit.from,
        depositTo: hexlify(depositTo),
        amount: BigNumber.from(deposit.amount),
        subAccountId: deposit.subAccountId,
        ethTxOptions: deposit.ethTxOptions,
      })
    } else {
      const approveNonce = await sendApproveFromEthereum(provider, {
        from: deposit.from,
        mainContract,
        tokenAddress: deposit.token,
        amount: BigNumber.from(deposit.amount),
      })

      // If deposit from Ethereum, must be using the gateway interface.
      const instance = gateway
        ? ZKLINK_GATEWAY_CONTRACT_INTERFACE
        : ZKLINK_MAIN_CONTRACT_INTERFACE
      const data = instance.encodeFunctionData('depositERC20', [
        deposit.token,
        deposit.amount,
        depositTo,
        deposit.subAccountId,
        deposit.mapping,
      ])

      const tx = {
        from: deposit.from,
        to: mainContract,
        data,
        value: bn(0),
        ...deposit.ethTxOptions,
      }

      if (gateway) {
        const estValue = await estimateDepositErc20FeeValue(
          provider,
          instance,
          mainContract,
        )
        tx.value = bn(estValue)
      }

      if (approveNonce) {
        tx.nonce = approveNonce + 1
      }

      // We set gas limit only if after approve,
      // because the deposit after approve is a nonce jumping transaction,
      // the metamask will not automatically set the gas limit.
      if (approveNonce) {
        tx.gasLimit = getRecommendedDepositGasLimit(deposit.chainId)
      }

      response = await provider.getSigner().sendTransaction(tx)
    }

    // response.wait(1).then(onDepositConfirmation)

    return {
      chainId: deposit.chainId,
      from: response.from,
      hash: response.hash,
    }
  } catch (e) {
    return Promise.reject(e)
  } finally {
    store.dispatch(updateDepositingAction(false))
  }
}
